import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Plus,
  Search,
  Trash2,
  X,
  RefreshCw,
  Phone,
  Award,
  Calendar,
  Minus,
  Mail,
  ChevronRight,
  ChevronDown,
  RotateCcw,
  ShoppingBag,
} from "lucide-react";
import api, { ApiResponse } from "../../services/api";
import { toast } from "react-hot-toast";
import { activeCurrencySymbol } from "../../utils/currency";

interface LoyaltyCustomer {
  _id: string;
  name: string;
  email: string;
  phoneNumber: string;
  points: number;
  createdAt: string;
  spendAmount: number;
  bonusWallet: { balance: number; pendingBalance: number; currency: string };
  blackListForCash: boolean;
  blackListForCard: boolean;
}

interface OrderItem {
  name: string;
  quantity: number;
  price: number;
}

interface CompensationEntry {
  points: number;
  reason: string;
  restaurantName?: string;
  restaurantAddress?: string;
  createdAt: string;
}

interface Order {
  _id: string;
  orderNumber: string;
  items: OrderItem[];
  totalAmount: number;
  timestamp: string;
  orderStatus?: string;
  choice?: string;
  restaurantName?: string;
  compensationHistory?: CompensationEntry[];
}

const LoyaltyManagement: React.FC = () => {
  const [customers, setCustomers] = useState<LoyaltyCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const customersPerPage = 25;

  // Existing add/remove modal states
  const [editingCustomer, setEditingCustomer] = useState<LoyaltyCustomer | null>(null);
  const [points, setPoints] = useState("");
  const [actionType, setActionType] = useState<"add" | "remove">("add");

  // User orders modal
  const [selectedUser, setSelectedUser] = useState<LoyaltyCustomer | null>(null);
  const [showUserOrdersModal, setShowUserOrdersModal] = useState(false);
  const [userOrders, setUserOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersLoadingMore, setOrdersLoadingMore] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersHasMore, setOrdersHasMore] = useState(false);
  // Refs that don't trigger re-renders
  const ordersSearchRef = useRef(""); // always tracks latest committed search
  const ordersDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null); // cancel in-flight requests
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const ordersScrollRef = useRef<HTMLDivElement | null>(null);

  // Add manual points modal (inside user orders modal)
  const [showAddPointsModal, setShowAddPointsModal] = useState(false);
  const [manualPoints, setManualPoints] = useState("");
  const [manualReason, setManualReason] = useState("");
  const [manualLoading, setManualLoading] = useState(false);

  // Inline compensation accordion
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [compensationPoints, setCompensationPoints] = useState("");
  const [compensationReason, setCompensationReason] = useState("");
  const [compensationLoading, setCompensationLoading] = useState(false);

  useEffect(() => {
    fetchCustomers(currentPage);
  }, [currentPage]);

  const fetchCustomers = async (page = 1, search = "") => {
    try {
      setLoading(true);
      setError(null);
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      const response = await api.get<ApiResponse<{ data: LoyaltyCustomer[]; total: number }>>(
        `/loyalty/users?page=${page}&limit=${customersPerPage}${searchParam}`
      );

      if (response.data.success) {
        const sorted = [...response.data.data.data].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setCustomers(sorted);
        setTotalPages(Math.ceil(response.data.data.total / customersPerPage));
      } else {
        setCustomers([]);
        setError("Failed to load loyalty customers.");
      }
    } catch {
      setError("Failed to load loyalty customers. Please try again.");
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePointsUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCustomer) return;

    let pointsValue = parseFloat(points);
    if (isNaN(pointsValue) || pointsValue <= 0) {
      toast.error("Please enter a valid positive number of points.");
      return;
    }
    pointsValue = parseFloat(pointsValue.toFixed(2));

    try {
      setError(null);
      const endpoint = actionType === "add" ? "/loyalty/add" : "/loyalty/remove";
      await api.post<ApiResponse<LoyaltyCustomer>>(endpoint, {
        phoneNumber: editingCustomer.phoneNumber,
        points: pointsValue,
      });

      setShowModal(false);
      toast.success(`Points ${actionType === "add" ? "added" : "removed"} successfully!`);
      const delta = actionType === "add" ? pointsValue : -pointsValue;
      const newBalance = parseFloat(((editingCustomer.bonusWallet?.balance ?? 0) + delta).toFixed(2));
      setCustomers((prev) => prev.map((c) => c._id === editingCustomer._id ? { ...c, bonusWallet: { ...c.bonusWallet, balance: newBalance } } : c));
      if (selectedUser?._id === editingCustomer._id) {
        setSelectedUser((prev) => prev ? { ...prev, bonusWallet: { ...prev.bonusWallet, balance: newBalance } } : prev);
      }
      resetForm();
    } catch {
      setError("Failed to update points. Please try again.");
      toast.error("Failed to update points.");
    }
  };

  const handleToggleFlag = async (
    customer: LoyaltyCustomer,
    flag: "blackListForCash" | "blackListForCard"
  ) => {
    const newValue = !customer[flag];
    try {
      await api.patch(`/loyalty/users/${customer.phoneNumber}/flags`, { [flag]: newValue });
      setCustomers((prev) =>
        prev.map((c) => c._id === customer._id ? { ...c, [flag]: newValue } : c)
      );
      if (selectedUser?._id === customer._id) {
        setSelectedUser((prev) => prev ? { ...prev, [flag]: newValue } : prev);
      }
      toast.success(`${flag === "blackListForCash" ? "Cash" : "Card"} blacklist ${newValue ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Failed to update blacklist flag.");
    }
  };

  const handleDelete = async (phoneNumber: string) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this customer? This action cannot be undone.`
    );
    if (!confirmDelete) return;

    try {
      setError(null);
      await api.delete(`/loyalty/${phoneNumber}`);
      fetchCustomers(currentPage);
      toast.success("Customer deleted successfully!");
    } catch {
      toast.error("Points already 0");
    }
  };

  const handleEdit = (customer: LoyaltyCustomer, type: "add" | "remove") => {
    setEditingCustomer(customer);
    setActionType(type);
    setPoints("");
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setPoints("");
    setActionType("add");
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;
    const cleaned = input.replace(/[^\d-]/g, "");
    const digits = cleaned.replace(/-/g, "").slice(0, 10);
    let formatted = "";
    const validManualFormat =
      /^\d{1,3}$|^\d{3}-\d{0,3}$|^\d{3}-\d{3}-\d{0,4}$/.test(cleaned);

    if (validManualFormat && cleaned === input.replace(/[^\d-]/g, "")) {
      formatted = cleaned;
    } else {
      if (digits.length > 6) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length > 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        formatted = digits;
      }
    }
    setSearchTerm(formatted);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanPhoneNumber = searchTerm.replace(/-/g, "");
    setCurrentPage(1);
    fetchCustomers(1, cleanPhoneNumber);
  };

  const refreshData = () => {
    setSearchTerm("");
    setCurrentPage(1);
    fetchCustomers(1, "");
  };

  /**
   * Core fetch — always cancels the previous in-flight request via AbortController.
   * Only updates state if the response belongs to the latest request (not a stale one).
   */
  const fetchOrders = useCallback(async (
    phoneNumber: string,
    search: string,
    page: number,
    append: boolean,
  ) => {
    // Cancel any previous in-flight request
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (page === 1) setOrdersLoading(true);
    else setOrdersLoadingMore(true);

    try {
      const params = new URLSearchParams({ page: String(page) });
      if (search) params.set('search', search);
      const res = await api.get<ApiResponse<Order[]> & { total: number; page: number; hasMore: boolean }>(
        `/loyalty/orders/${phoneNumber}?${params}`,
        { signal: controller.signal },
      );
      // Guard: ignore if this controller was already replaced (another request started)
      if (abortControllerRef.current !== controller) return;
      if (res.data.success) {
        setUserOrders(prev => append ? [...prev, ...res.data.data] : res.data.data);
        setOrdersHasMore(res.data.hasMore);
        setOrdersPage(page);
      }
    } catch (err: any) {
      // Silently ignore aborted requests — not an error
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      toast.error("Failed to load orders.");
    } finally {
      // Only clear loading spinners for the request that is still "current"
      if (abortControllerRef.current === controller) {
        setOrdersLoading(false);
        setOrdersLoadingMore(false);
      }
    }
  }, []);

  // Open user orders modal
  const openUserOrders = async (customer: LoyaltyCustomer) => {
    if (ordersDebounceRef.current) clearTimeout(ordersDebounceRef.current);
    setSelectedUser(customer);
    setShowUserOrdersModal(true);
    setUserOrders([]);
    setOrderSearch("");
    ordersSearchRef.current = "";
    setOrdersPage(1);
    setOrdersHasMore(false);
    setExpandedOrderId(null);
    setCompensationPoints("");
    setCompensationReason("");
    await fetchOrders(customer.phoneNumber, "", 1, false);
  };

  const closeUserOrdersModal = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (ordersDebounceRef.current) clearTimeout(ordersDebounceRef.current);
    setShowUserOrdersModal(false);
    setSelectedUser(null);
    setUserOrders([]);
    setOrderSearch("");
    ordersSearchRef.current = "";
    setOrdersPage(1);
    setOrdersHasMore(false);
    setExpandedOrderId(null);
    setCompensationPoints("");
    setCompensationReason("");
  };

  /**
   * Debounced + deduplicated search.
   * - Debounce: 400ms — waits until user stops typing before firing
   * - Deduplication: skips the fetch if the term hasn't changed since the last fired request
   */
  const handleOrderSearch = (value: string) => {
    setOrderSearch(value);
    ordersSearchRef.current = value;
    if (ordersDebounceRef.current) clearTimeout(ordersDebounceRef.current);
    ordersDebounceRef.current = setTimeout(() => {
      const term = ordersSearchRef.current; // read latest value at fire time
      if (selectedUser) {
        setUserOrders([]);
        setOrdersPage(1);
        fetchOrders(selectedUser.phoneNumber, term, 1, false);
      }
    }, 400);
  };

  // Infinite scroll — IntersectionObserver watches a sentinel div at the bottom of the list.
  // Only fires when not already loading and there are more pages.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !ordersHasMore || ordersLoading || ordersLoadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && selectedUser && !ordersLoadingMore && !ordersLoading) {
          fetchOrders(selectedUser.phoneNumber, ordersSearchRef.current, ordersPage + 1, true);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [ordersHasMore, ordersLoading, ordersLoadingMore, ordersPage, selectedUser, fetchOrders]);

  // Manual add points
  const handleManualPoints = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    const pts = parseFloat(manualPoints);
    if (isNaN(pts) || pts <= 0) {
      toast.error("Enter a valid points amount.");
      return;
    }
    setManualLoading(true);
    try {
      await api.post("/loyalty/manual-points", {
        phoneNumber: selectedUser.phoneNumber,
        points: pts,
        reason: manualReason,
        restaurantId: process.env.REACT_APP_RESTAURANT_ID,
      });
      toast.success("Points added successfully!");
      setShowAddPointsModal(false);
      setManualPoints("");
      setManualReason("");
      // Update modal and card in-place immediately
      const newBalance = parseFloat(((selectedUser.bonusWallet?.balance ?? 0) + pts).toFixed(2));
      const updatedUser = { ...selectedUser, bonusWallet: { ...selectedUser.bonusWallet, balance: newBalance } };
      setSelectedUser(updatedUser);
      setCustomers((prev) => prev.map((c) => c._id === selectedUser._id ? { ...c, bonusWallet: { ...c.bonusWallet, balance: newBalance } } : c));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to add points.");
    } finally {
      setManualLoading(false);
    }
  };

  // Toggle accordion row; reset form when switching to a different order
  const toggleOrderExpand = (orderId: string) => {
    setExpandedOrderId(prev => {
      if (prev === orderId) return null;
      setCompensationPoints("");
      setCompensationReason("");
      return orderId;
    });
  };

  // Submit inline compensation
  const handleCompensation = async (e: React.FormEvent, order: Order) => {
    e.preventDefault();
    if (!selectedUser) return;
    const pts = parseFloat(compensationPoints);
    if (isNaN(pts) || pts <= 0) {
      toast.error("Enter a valid points amount.");
      return;
    }
    if (pts > order.totalAmount) {
      toast.error(`Points cannot exceed order total (${activeCurrencySymbol()}${order.totalAmount.toFixed(2)}).`);
      return;
    }
    setCompensationLoading(true);
    try {
      await api.post("/loyalty/order-refund", {
        phoneNumber: selectedUser.phoneNumber,
        orderId: order._id,
        points: pts,
        reason: compensationReason,
        restaurantId: process.env.REACT_APP_RESTAURANT_ID,
      });
      toast.success("Compensation points added!");
      setCompensationPoints("");
      setCompensationReason("");
      const newEntry: CompensationEntry = { points: pts, reason: compensationReason, createdAt: new Date().toISOString() };
      setUserOrders(prev =>
        prev.map(o => o._id === order._id
          ? { ...o, compensationHistory: [...(o.compensationHistory ?? []), newEntry] }
          : o)
      );
      const newBalance = parseFloat(((selectedUser.bonusWallet?.balance ?? 0) + pts).toFixed(2));
      setSelectedUser(u => u ? { ...u, bonusWallet: { ...u.bonusWallet, balance: newBalance } } : u);
      setCustomers(prev => prev.map(c => c._id === selectedUser._id ? { ...c, bonusWallet: { ...c.bonusWallet, balance: newBalance } } : c));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to process compensation.");
    } finally {
      setCompensationLoading(false);
    }
  };

  if (loading && customers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto p-4 pt-0 lg:p-8 flex flex-col overflow-y-auto no-scrollbar h-screen">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">User List</h1>
        </div>

        {/* ================= SEARCH ================= */}
        <div className="rounded-lg mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Search by Phone Number
          </label>
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Phone
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={handlePhoneNumberChange}
                placeholder="Search by phone number (xxx-xxx-xxxx)"
                maxLength={12}
                className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Search
            </button>
            <button
              type="button"
              onClick={refreshData}
              className="w-10 h-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
              title="Show All Customers"
            >
              <RefreshCw size={18} />
            </button>
          </form>
        </div>
      </div>

      {/* ================= ERROR ================= */}
      {error && (
        <div className="bg-red-500 text-white p-3 rounded-md mb-4 text-center">{error}</div>
      )}

      {/* ================= CONTENT ================= */}
      <div className="flex-1 relative">
        {loading && customers.length > 0 && (
          <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg shadow">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="text-sm text-gray-600">Loading...</span>
            </div>
          </div>
        )}
        {customers.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No customers found</p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Try searching with a different phone number"
                  : "No loyalty customers available"}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {customers.map((customer) => (
              <div
                key={customer._id}
                className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Card Header - Clickable to open orders */}
                <div
                  className="mb-4 cursor-pointer group"
                  onClick={() => openUserOrders(customer)}
                >
                  <div className="flex items-start justify-between">
                    <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                      {customer.name || "Unknown Customer"}
                    </h3>
                    <ChevronRight
                      size={16}
                      className="text-gray-400 group-hover:text-blue-600 mt-1 flex-shrink-0 transition-colors"
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone size={14} className="text-gray-400" />
                      <span>{customer.phoneNumber}</span>
                    </div>
                    {customer.email && (
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <Mail size={14} className="text-gray-400" />
                        <span>{customer.email}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Loyalty Section */}
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Award size={20} className="text-yellow-500" />
                      <span className="text-xl font-bold text-blue-600">
                        {customer.bonusWallet?.balance.toFixed(2) ?? 0}
                      </span>
                      <span className="text-sm text-gray-600">points</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-400 mt-2">
                    <Calendar size={12} />
                    <span>Joined {new Date(customer.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Blacklist flags */}
                <div className="mb-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-600">Blacklist Cash</span>
                    <button
                      onClick={() => handleToggleFlag(customer, "blackListForCash")}
                      className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none ${customer.blackListForCash ? "bg-red-500" : "bg-gray-300"}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${customer.blackListForCash ? "translate-x-5" : "translate-x-1"}`}
                      />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-600">Blacklist Card</span>
                    <button
                      onClick={() => handleToggleFlag(customer, "blackListForCard")}
                      className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none ${customer.blackListForCard ? "bg-red-500" : "bg-gray-300"}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${customer.blackListForCard ? "translate-x-5" : "translate-x-1"}`}
                      />
                    </button>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <button
                    onClick={() => handleDelete(customer.phoneNumber)}
                    className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition"
                  >
                    <Trash2 size={14} />
                    Delete User
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= PAGINATION ================= */}
      {totalPages > 1 && (
        <div className="py-4 flex justify-center">
          <div className="flex gap-2 items-center">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Prev
            </button>
            {(() => {
              const pages = [];
              const showEllipsis = totalPages > 7;
              if (!showEllipsis) {
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === i
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {i}
                    </button>
                  );
                }
              } else {
                const startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(totalPages, currentPage + 2);
                if (startPage > 1) {
                  pages.push(
                    <button
                      key={1}
                      onClick={() => setCurrentPage(1)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === 1 ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      1
                    </button>
                  );
                  if (startPage > 2) {
                    pages.push(
                      <span key="ellipsis1" className="px-2 py-1 text-gray-500">
                        ...
                      </span>
                    );
                  }
                }
                for (let i = startPage; i <= endPage; i++) {
                  if (i !== 1 && i !== totalPages) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1 rounded transition ${
                          currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                }
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <span key="ellipsis2" className="px-2 py-1 text-gray-500">
                        ...
                      </span>
                    );
                  }
                  pages.push(
                    <button
                      key={totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === totalPages ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {totalPages}
                    </button>
                  );
                }
              }
              return pages;
            })()}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ================= EXISTING ADD/REMOVE MODAL ================= */}
      {showModal && editingCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col mx-auto">
            <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800 mb-0">
                {actionType === "add" ? "Add Points" : "Remove Points"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <form id="points-form" onSubmit={handlePointsUpdate} className="p-6">
                <div className="space-y-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Phone size={18} className="text-blue-600" />
                      <span className="font-semibold text-gray-800">
                        {editingCustomer.phoneNumber}
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Award size={18} className="text-yellow-500" />
                      <span className="text-lg font-bold text-blue-600">
                        Current: {editingCustomer.bonusWallet?.balance ?? 0} points
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Points to {actionType} *
                    </label>
                    <input
                      type="number"
                      value={points}
                      onChange={(e) => setPoints(e.target.value)}
                      placeholder="Enter points amount"
                      required
                      step="0.01"
                      min="0.01"
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div
                    className={`p-3 rounded-md border ${
                      actionType === "add"
                        ? "bg-green-50 border-green-200"
                        : "bg-orange-50 border-orange-200"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {actionType === "add" ? (
                        <Plus size={16} className="text-green-600" />
                      ) : (
                        <Minus size={16} className="text-orange-600" />
                      )}
                      <span
                        className={`text-sm font-medium ${
                          actionType === "add" ? "text-green-800" : "text-orange-800"
                        }`}
                      >
                        {actionType === "add"
                          ? "This will add points to the customer's account"
                          : "This will remove points from the customer's account"}
                      </span>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-lg flex justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="points-form"
                className={`px-5 py-2 text-white rounded-md transition ${
                  actionType === "add"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-orange-600 hover:bg-orange-700"
                }`}
              >
                {actionType === "add" ? "Add Points" : "Remove Points"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= USER ORDERS MODAL ================= */}
      {showUserOrdersModal && selectedUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={closeUserOrdersModal}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col mx-auto">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex-shrink-0">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-0">
                    {selectedUser.name || "Unknown Customer"}
                  </h2>
                  <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                    <Phone size={12} />
                    {selectedUser.phoneNumber}
                    <span className="ml-2 flex items-center gap-1">
                      <Award size={12} className="text-yellow-500" />
                      <span className="font-medium text-blue-600">{selectedUser.bonusWallet?.balance ?? 0} pts</span>
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowAddPointsModal(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition"
                  >
                    <Plus size={14} />
                     <span className="sm:hidden">Add</span>
                     <span className="hidden sm:inline">Add Points</span>
                  </button>
                  <button
                    type="button"
                    onClick={closeUserOrdersModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                <input
                  type="text"
                  value={orderSearch}
                  onChange={(e) => handleOrderSearch(e.target.value)}
                  placeholder="Search by order number..."
                  className="pl-9 pr-4 py-2 w-full border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                />
                {orderSearch && (
                  <button
                    onClick={() => handleOrderSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Orders list */}
            <div className="flex-1 overflow-y-auto">
              {ordersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : userOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  {orderSearch ? (
                    <>
                      <Search size={40} className="mb-3 opacity-40" />
                      <p className="font-medium">No orders match "{orderSearch}"</p>
                    </>
                  ) : (
                    <>
                      <ShoppingBag size={40} className="mb-3 opacity-40" />
                      <p className="font-medium">No orders found</p>
                      <p className="text-sm mt-1">This customer has no orders yet.</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="p-4 space-y-2">
                  {userOrders.map((order) => {
                    const isExpanded = expandedOrderId === order._id;
                    const history = order.compensationHistory ?? [];
                    const totalCompensated = history.reduce((sum, c) => sum + c.points, 0);
                    const compensationPointsNum = parseFloat(compensationPoints);
                    const exceedsMax = isExpanded && !isNaN(compensationPointsNum) && compensationPointsNum > order.totalAmount;

                    return (
                      <div
                        key={order._id}
                        className={`border rounded-lg overflow-hidden transition-colors ${
                          isExpanded ? "border-blue-300 shadow-sm" : "border-gray-200"
                        }`}
                      >
                        {/* Row header — click anywhere to toggle */}
                        <button
                          type="button"
                          onClick={() => toggleOrderExpand(order._id)}
                          className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-gray-800 text-sm">#{order.orderNumber}</span>
                                {order.orderStatus && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    order.orderStatus === "completed" || order.orderStatus === "delivered" || order.orderStatus === "pickedup"
                                      ? "bg-green-100 text-green-700"
                                      : "bg-yellow-100 text-yellow-700"
                                  }`}>
                                    {order.orderStatus}
                                  </span>
                                )}
                                {history.length > 0 && (
                                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">
                                    {history.length} compensation{history.length > 1 ? "s" : ""}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {new Date(order.timestamp).toLocaleString()} · {order.items.length} item{order.items.length !== 1 ? "s" : ""}{order.choice ? ` · ${order.choice}` : ""}
                              </p>
                              {order.restaurantName && (
                                <p className="text-xs text-gray-500 font-medium mt-0.5">{order.restaurantName}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                            <span className="font-bold text-gray-800">{activeCurrencySymbol()}{order.totalAmount.toFixed(2)}</span>
                            <ChevronDown
                              size={18}
                              className={`text-gray-400 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
                            />
                          </div>
                        </button>

                        {/* Accordion body */}
                        <div className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${isExpanded ? "max-h-[1200px]" : "max-h-0"}`}>
                          <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-4 space-y-4">

                            {/* Order items */}
                            <div>
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Order Items</p>
                              <div className="space-y-1">
                                {order.items.map((item: OrderItem, i: number) => (
                                  <div key={i} className="flex justify-between text-sm">
                                    <span className="text-gray-700">{item.quantity}× {item.name}</span>
                                    <span className="text-gray-600 font-medium">{activeCurrencySymbol()}{(item.price * item.quantity).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                              <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between font-bold text-gray-800 text-sm">
                                <span>Order Total</span>
                                <span>{activeCurrencySymbol()}{order.totalAmount.toFixed(2)}</span>
                              </div>
                            </div>

                            {/* Compensation history */}
                            {history.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Previous Compensations</p>
                                <div className="space-y-2">
                                  {history.map((entry, idx) => (
                                    <div key={idx} className="flex items-start justify-between gap-3">
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-700 font-medium">{entry.reason}</p>
                                        {entry.restaurantName && (
                                          <p className="text-xs text-gray-500">{entry.restaurantName}{entry.restaurantAddress ? ` · ${entry.restaurantAddress}` : ""}</p>
                                        )}
                                        <p className="text-xs text-gray-400">{new Date(entry.createdAt).toLocaleDateString()}</p>
                                      </div>
                                      <span className="text-sm font-semibold text-purple-700 whitespace-nowrap">{activeCurrencySymbol()}{entry.points.toFixed(2)}</span>
                                    </div>
                                  ))}
                                  {history.length > 1 && (
                                    <div className="pt-2 border-t border-gray-200 flex justify-between">
                                      <span className="text-sm font-semibold text-gray-600">Total compensated</span>
                                      <span className="text-sm font-semibold text-purple-700">{activeCurrencySymbol()}{totalCompensated.toFixed(2)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Compensation form */}
                            <form onSubmit={(e) => handleCompensation(e, order)}>
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Add Compensation</p>
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                  <label className="block mb-1.5 text-xs font-medium text-gray-700">
                                    Points <span className="text-gray-400 font-normal">(max {activeCurrencySymbol()}{order.totalAmount.toFixed(2)})</span>
                                  </label>
                                  <input
                                    type="number"
                                    value={compensationPoints}
                                    onChange={(e) => setCompensationPoints(e.target.value)}
                                    placeholder="0.00"
                                    required
                                    step="0.01"
                                    min="0.01"
                                    max={order.totalAmount}
                                    className={`w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 transition-all ${
                                      exceedsMax ? "border-red-400 focus:ring-red-400 bg-red-50" : "border-gray-300 focus:ring-blue-500"
                                    }`}
                                  />
                                  {exceedsMax && <p className="mt-1 text-xs text-red-600">Cannot exceed {activeCurrencySymbol()}{order.totalAmount.toFixed(2)}</p>}
                                </div>
                                <div>
                                  <label className="block mb-1.5 text-xs font-medium text-gray-700">Reason *</label>
                                  <input
                                    type="text"
                                    value={compensationReason}
                                    onChange={(e) => setCompensationReason(e.target.value)}
                                    placeholder="e.g. Wrong order..."
                                    required
                                    className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                  />
                                </div>
                              </div>
                              <button
                                type="submit"
                                disabled={compensationLoading || exceedsMax}
                                className="w-full flex items-center justify-center gap-2 py-2 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 transition disabled:opacity-60"
                              >
                                <RotateCcw size={14} />
                                {compensationLoading ? "Processing..." : "Add Compensation"}
                              </button>
                            </form>

                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {/* Infinite scroll sentinel */}
                  <div ref={sentinelRef} className="h-2" />
                  {ordersLoadingMore && (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                    </div>
                  )}
                  {!ordersHasMore && userOrders.length > 0 && (
                    <p className="text-center text-xs text-gray-400 py-3">All orders loaded</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ================= ADD POINTS MODAL ================= */}
      {showAddPointsModal && selectedUser && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowAddPointsModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col mx-auto">
            <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800 mb-0">Add Points</h2>
              <button
                type="button"
                onClick={() => setShowAddPointsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <form id="manual-points-form" onSubmit={handleManualPoints} className="p-6">
                <div className="space-y-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Phone size={18} className="text-blue-600" />
                      <span className="font-semibold text-gray-800">{selectedUser.phoneNumber}</span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Award size={18} className="text-yellow-500" />
                      <span className="text-lg font-bold text-blue-600">
                        Current: {selectedUser.bonusWallet?.balance ?? 0} points
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Points to Add *
                    </label>
                    <input
                      type="number"
                      value={manualPoints}
                      onChange={(e) => setManualPoints(e.target.value)}
                      placeholder="e.g. 50"
                      required
                      step="0.01"
                      min="0.01"
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Reason *
                    </label>
                    <input
                      type="text"
                      value={manualReason}
                      onChange={(e) => setManualReason(e.target.value)}
                      placeholder="e.g. Compensation, promotion..."
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </form>
            </div>
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-lg flex justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowAddPointsModal(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="manual-points-form"
                disabled={manualLoading}
                className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-60"
              >
                {manualLoading ? "Saving..." : "Add Points"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LoyaltyManagement;
